(() => {
  const root = document.getElementById('WishlistGrid');
  if (!root) return;

  const KEY = 'wb_wishlist_v1';
  const base = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');

  // ---------- Toast ----------
  const toast = (() => {
    let el, t;
    return (msg) => {
      if (!el) {
        el = document.createElement('div');
        el.className = 'wb-wl-toast';
        document.body.appendChild(el);
      }
      el.textContent = msg;
      el.classList.add('show');
      clearTimeout(t);
      t = setTimeout(() => el.classList.remove('show'), 1100);
    };
  })();

  // ---------- Safe wishlist access ----------
  const readItems = () => {
    try {
      if (window.WBWishlist && typeof window.WBWishlist.read === 'function') {
        const items = window.WBWishlist.read();
        return Array.isArray(items) ? items : [];
      }
    } catch {}

    try {
      const raw = localStorage.getItem(KEY);
      const items = raw ? JSON.parse(raw) : [];
      return Array.isArray(items) ? items : [];
    } catch {
      return [];
    }
  };

  const removeItem = (variantId) => {
    const vid = String(variantId);
    try {
      if (window.WBWishlist?.remove) return window.WBWishlist.remove(vid);
    } catch {}

    const next = readItems().filter((i) => String(i.variantId) !== vid);
    try {
      localStorage.setItem(KEY, JSON.stringify(next));
      window.dispatchEvent(new CustomEvent('wishlist:changed', { detail: { items: next } }));
    } catch {}
    return next;
  };

  // ---------- Helpers ----------
  const esc = (s) =>
    String(s ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const safeUrl = (u) => {
    const s = String(u || '').trim();
    if (!s) return '#';
    if (s.startsWith('/') || s.startsWith('http://') || s.startsWith('https://')) return s;
    return '#';
  };

  const getCartDrawer = () => document.querySelector('cart-drawer');

  // Must match your cart-drawer.js ids
  const getDrawerSectionIds = () => {
    const drawer = getCartDrawer();
    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      try {
        const ids = drawer.getSectionsToRender().map((s) => s.id).filter(Boolean);
        if (ids.length) return ids;
      } catch {}
    }
    return ['cart-drawer', 'cart-icon-bubble', 'cart-icon-bubble-sticky'];
  };

  // ✅ Correctly set empty state using REAL cart.js item_count
  const syncDrawerEmptyStateFromCart = async (drawer) => {
    if (!drawer) return;

    try {
      const cart = await fetch(`${base}cart.js`, { cache: 'no-store' }).then((r) => r.json());
      const isEmpty = Number(cart?.item_count || 0) === 0;

      drawer.classList.toggle('is-empty', isEmpty);

      const inner = drawer.querySelector('.drawer__inner');
      if (inner) inner.classList.toggle('is-empty', isEmpty);
    } catch {
      // If cart.js fails, don't force an empty state (safer than wrong is-empty)
      drawer.classList.remove('is-empty');
      drawer.querySelector('.drawer__inner')?.classList.remove('is-empty');
    }
  };

  // ✅ Reset drawer scroll AFTER it opens (transition end)
  const enforceDrawerTopAfterOpen = (drawer) => {
    if (!drawer) return;

    const doReset = () => {
      const inner = drawer.querySelector('.drawer__inner');
      if (inner) inner.scrollTop = 0;

      // keep focus on close button to avoid scroll-to-last-focused weirdness
      drawer.querySelector('.drawer__close')?.focus?.();
    };

    // Run now (sometimes it works)
    requestAnimationFrame(doReset);
    // Run a bit later (after render swap)
    setTimeout(doReset, 120);
    // Run after transition completes (MOST reliable)
    drawer.addEventListener(
      'transitionend',
      () => setTimeout(doReset, 0),
      { once: true }
    );
  };

  // ---------- Render wishlist ----------
  const render = () => {
    const items = readItems();

    if (!items.length) {
      root.innerHTML = `<p class="wb-wl-empty">Your wishlist is empty.</p>`;
      return;
    }

    root.innerHTML = `<div class="wb-wl-grid">
      ${items
        .map((i) => {
          const vid = String(i.variantId);
          const href = safeUrl(i.url);
          const title = esc(i.title || i.handle || 'Product');
          const img = i.image ? `<img loading="lazy" src="${esc(i.image)}" alt="${title}">` : ``;
          const price = esc(i.price || '');

          return `
            <article class="wb-wl-card" data-wl-card="${esc(vid)}">
              <a class="wb-wl-media" href="${href}">${img}</a>
              <div class="wb-wl-meta">
                <a class="wb-wl-title" href="${href}">${title}</a>
                <div class="wb-wl-price">${price}</div>
                <div class="wb-wl-actions">
                  <button class="wb-btn" data-wl-move data-variant-id="${esc(vid)}">Move to cart</button>
                  <button class="wb-btn wb-btn-ghost" data-wl-remove data-variant-id="${esc(vid)}">Remove</button>
                </div>
              </div>
            </article>
          `;
        })
        .join('')}
    </div>`;
  };

  // ---------- Add to cart + update drawer via theme renderer ----------
  const addToCartAndUpdateDrawer = async (variantId) => {
    const id = parseInt(String(variantId), 10);
    if (!Number.isFinite(id)) throw new Error('Invalid variant id');

    const drawer = getCartDrawer();
    const sections = getDrawerSectionIds();
    const addUrl = (window.routes?.cart_add_url) || `${base}cart/add.js`;

    const res = await fetch(addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id, quantity: 1 }],
        sections,
        sections_url: window.location.pathname,
      }),
    });

    const data = await res.json().catch(() => null);

    if (!res.ok) {
      throw new Error(data?.description || data?.message || 'Add to cart failed');
    }

    if (drawer && typeof drawer.renderContents === 'function' && data?.sections) {
      // Let the theme do its update + open
      drawer.renderContents(data);

      // ✅ Fix empty-state logic using real cart count
      await syncDrawerEmptyStateFromCart(drawer);

      // ✅ Prevent footer-only view (scroll jump)
      enforceDrawerTopAfterOpen(drawer);
    }

    return true;
  };

  // ---------- Click handling ----------
  root.addEventListener('click', async (e) => {
    const rem = e.target.closest('[data-wl-remove]');
    const move = e.target.closest('[data-wl-move]');
    if (!rem && !move) return;

    const variantId = String((rem || move).dataset.variantId || '');

    if (rem) {
      root.querySelector(`[data-wl-card="${CSS.escape(variantId)}"]`)?.remove();
      removeItem(variantId);
      toast('Removed from wishlist');
      return;
    }

    const btn = move;
    const card = root.querySelector(`[data-wl-card="${CSS.escape(variantId)}"]`);
    const oldText = btn.textContent;

    btn.textContent = 'Adding...';
    btn.disabled = true;

    try {
      await addToCartAndUpdateDrawer(variantId);

      card?.remove();
      removeItem(variantId);

      toast('Added to cart');
    } catch (err) {
      toast(err?.message || 'Could not add to cart');
    } finally {
      btn.textContent = oldText;
      btn.disabled = false;
    }
  });

  // ---------- Init (retry for script-order issues) ----------
  const initWithRetry = () => {
    let tries = 0;

    const tick = () => {
      render();

      const hasStore = !!window.WBWishlist?.read;
      const hasItems = readItems().length > 0;

      tries += 1;
      if (!hasStore && hasItems && tries < 30) {
        setTimeout(tick, 100);
      }
    };

    tick();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initWithRetry);
  } else {
    initWithRetry();
  }

  window.addEventListener('wishlist:changed', render);
  window.addEventListener('storage', (ev) => {
    if (ev.key === KEY) render();
  });
})();
